/**
 * Running a local relay server will allow you to hide your API key
 * and run custom logic on the server
 *
 * Set the local relay server address to:
 * REACT_APP_LOCAL_RELAY_SERVER_URL=http://localhost:8081
 *
 * This will also require you to set OPENAI_API_KEY= in a `.env` file
 * You can run it with `npm run relay`, in parallel with `npm start`
 */

import { useCallback, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Session } from '../agent/session';
import Logo from '../assets/images/Logo.svg';
import FooterLogo from '../assets/images/Footer_Logo.svg';
import XLink from '../assets/images/X_Link.svg';
import './EntrancePage.scss';

interface EntranceProps {
  sessionRef: React.MutableRefObject<Session>;
  onConnect: () => void;
}

export function EntrancePage({ sessionRef, onConnect }: EntranceProps) {
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnect = useCallback(() => {
    setIsConnecting(true);
    sessionRef.current.connect(() => {
      setIsConnecting(false);
      onConnect();
    }).catch(() => {
      setIsConnecting(false);
    });
  }, [sessionRef, onConnect]);

  /**
   * Render the application
   */
  return (
    <div data-component="EntrancePage">
      <div className="content-top">
        <div className="content-title">
          <div className="title">
            <img src={Logo} alt="The Gospel of Trump" />
          </div>
          <button
            className="launch-button"
            onClick={handleConnect}
            disabled={isConnecting}
          >
            {isConnecting ? (
              <>
                <div className="spinner" />
                Launching...
              </>
            ) : (
              'LAUNCH APP'
            )}
          </button>
        </div>
      </div>

      <div className="content-main">
        <div className="text-content">
          <h1>
            THE #1<br />
            <span className="highlight">IMMERSIVE</span><br />
            AI AGENT
          </h1>
          <h2><span className="highlight">COMMUNITY-DRIVEN, sharing the leader's wisdom in REAL-TIME</span></h2>
          <p>Join the Community. This is History in the Making!</p>
          <div className="button-group">
            <button
              className="primary-button"
              onClick={handleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <div className="spinner" />
                  LAUNCHING...
                </>
              ) : (
                'LAUNCH APP'
              )}
            </button>
            {/* TODO: add lite paper later */}
            {/* <button className="secondary-button">
              LITEPAPER
            </button> */}
          </div>
        </div>
        <div className="image-container">
          <LazyLoadImage
            src={require('../assets/images/trump_image@2x.webp')}
            srcSet={`
              ${require('../assets/images/trump_image_mobile.webp')} 800w,
              ${require('../assets/images/trump_image@2x.webp')} 1040w
            `}
            sizes="(max-width: 1024px) 100vw, 1040px"
            placeholderSrc={require('../assets/images/trump_image_placeholder.webp')}
            alt="Trump"
            className="trump-image"
            effect="blur"
            loading="lazy"
            width="100%"
            height="auto"
          />
        </div>
      </div>

      <div className="content-footer">
        <img src={FooterLogo} alt="Footer Logo" className="footer-logo" />
        <div className="copyright">© 2025 TrumpGospel.ai. All rights reserved.</div>
        <a href="https://x.com/realTrumpGospel" target="_blank" rel="noopener noreferrer">
          <img src={XLink} alt="X Social Link" className="x-link"/>
        </a>
      </div>
    </div>
  );
}
